import type {
  Bounds,
  ConnectionPoint,
  Handle,
  HandlePosition,
  Point,
  ShapeStyle,
  ShapeType,
  Size,
  TextStyle,
} from '../state/types';
import { DEFAULT_SHAPE_STYLE, DEFAULT_TEXT_STYLE } from '../state/types';
import { buildElementJSON } from '../state/elementSerialization';
import {
  fitTextToBox,
  getCenteredTextStartY,
  renderFormattedTextLines,
  resolveTextAlignPosition,
  wrapText,
} from '../tools/textLayout';
import { parseTextLine, getFontSizeMultiplier } from '../textEditor/TextFormatter';
import { CLIPBOARD, LINE_HEIGHT_RATIO, PADDING, SHAPE } from '../../../config/constants';
import { generateId } from '../../../utils/id';
import { getHandleAtPoint as getHandleAtPointFromHandles } from './handleUtils';
import { renderSelection } from './selectionRender';

const RESIZE_MULTIPLIERS: Record<HandlePosition, { posX: number; posY: number; width: number; height: number }> = {
  'top-left': { posX: 1, posY: 1, width: -1, height: -1 },
  'top': { posX: 0, posY: 1, width: 0, height: -1 },
  'top-right': { posX: 0, posY: 1, width: 1, height: -1 },
  'right': { posX: 0, posY: 0, width: 1, height: 0 },
  'bottom-right': { posX: 0, posY: 0, width: 1, height: 1 },
  'bottom': { posX: 0, posY: 0, width: 0, height: 1 },
  'bottom-left': { posX: 1, posY: 0, width: -1, height: 1 },
  'left': { posX: 1, posY: 0, width: -1, height: 0 },
};

export abstract class Shape {
  readonly id: string;
  abstract readonly type: ShapeType;
  position: Point;
  size: Size;
  rotation: number;
  style: ShapeStyle;
  textStyle: TextStyle;
  text: string;
  selected: boolean;

  constructor(
    position: Point,
    size: Size,
    style?: Partial<ShapeStyle>,
    text = ''
  ) {
    this.id = generateId('shape');
    this.position = { ...position };
    this.size = { ...size };
    this.rotation = 0;
    this.style = { ...DEFAULT_SHAPE_STYLE, ...style };
    this.textStyle = { ...DEFAULT_TEXT_STYLE, textAlign: 'center', color: this.style.stroke };
    this.text = text;
    this.selected = false;
  }

  abstract containsPoint(p: Point): boolean;
  abstract render(ctx: CanvasRenderingContext2D): void;
  abstract getConnectionPoints(): ConnectionPoint[];

  getBounds(): Bounds {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.width,
      height: this.size.height,
    };
  }

  getCenter(): Point {
    return {
      x: this.position.x + this.size.width / 2,
      y: this.position.y + this.size.height / 2,
    };
  }

  getHandles(): Handle[] {
    const { x, y } = this.position;
    const { width, height } = this.size;

    return [
      { position: 'top-left', point: { x, y } },
      { position: 'top', point: { x: x + width / 2, y } },
      { position: 'top-right', point: { x: x + width, y } },
      { position: 'right', point: { x: x + width, y: y + height / 2 } },
      { position: 'bottom-right', point: { x: x + width, y: y + height } },
      { position: 'bottom', point: { x: x + width / 2, y: y + height } },
      { position: 'bottom-left', point: { x, y: y + height } },
      { position: 'left', point: { x, y: y + height / 2 } },
    ];
  }

  getHandleAtPoint(p: Point, scale = 1): HandlePosition | null {
    return getHandleAtPointFromHandles(this.getHandles(), p, SHAPE.handleThreshold / scale);
  }

  resize(handle: HandlePosition, delta: Point): void {
    const m = RESIZE_MULTIPLIERS[handle];
    this.position.x += delta.x * m.posX;
    this.position.y += delta.y * m.posY;
    this.size.width += delta.x * m.width;
    this.size.height += delta.y * m.height;

    if (this.size.width < SHAPE.minSize) this.size.width = SHAPE.minSize;
    if (this.size.height < SHAPE.minSize) this.size.height = SHAPE.minSize;
  }

  move(delta: Point): void {
    this.position.x += delta.x;
    this.position.y += delta.y;
  }

  renderSelection(ctx: CanvasRenderingContext2D): void {
    if (!this.selected) return;

    const bounds = this.getBounds();
    renderSelection(
      ctx,
      bounds,
      this.getHandles().map((handle) => handle.point),
      SHAPE.handleSize
    );
  }

  // Calculate the font size needed to fit text within the shape
  calculateFittingFontSize(ctx: CanvasRenderingContext2D, text: string): number {
    if (!text) return this.textStyle.fontSize;

    const padding = PADDING.shapeText;
    const maxWidth = this.size.width - padding * 2;
    const maxHeight = this.size.height - padding * 2;
    const { fontWeight, fontStyle, fontSize, fontFamily } = this.textStyle;

    const layout = fitTextToBox(
      ctx,
      text,
      maxWidth,
      maxHeight,
        {
          fontSize,
          fontWeight,
          fontStyle,
          fontFamily,
        },
      8,
      LINE_HEIGHT_RATIO
    );

    return layout.fontSize;
  }

  renderText(ctx: CanvasRenderingContext2D): void {
    if (!this.text) return;

    const { fontWeight, fontStyle, textAlign, color, fontFamily } = this.textStyle;
    // Always use fitted font size to ensure text fits within shape bounds
    const fontSize = this.calculateFittingFontSize(ctx, this.text);
    const bounds = this.getBounds();
    const center = this.getCenter();
    const padding = PADDING.shapeText;
    const maxWidth = this.size.width - padding * 2;

    ctx.fillStyle = color;
    ctx.textAlign = textAlign;
    ctx.textBaseline = 'middle';

    // Parse lines to handle headers with proper wrapping
    const rawLines = this.text.split('\n');
    let allLines: string[] = [];

    for (const line of rawLines) {
      const format = parseTextLine(line);
      const multiplier = getFontSizeMultiplier(format.type);
      const adjustedFontSize = fontSize * multiplier;
      const adjustedFont = `${fontStyle} ${fontWeight} ${adjustedFontSize}px ${fontFamily}`;

      // Reduce available width for list items to account for bullet/number
      let availableWidth = maxWidth;
      if (format.type === 'unordered') {
        availableWidth -= adjustedFontSize * 0.8; // Bullet space
      } else if (format.type === 'ordered' && format.listNumber !== undefined) {
        ctx.font = adjustedFont;
        const numberWidth = ctx.measureText(`${format.listNumber}.`).width;
        availableWidth -= numberWidth + adjustedFontSize * 0.4; // Number space
      }

      const wrappedLines = wrapText(ctx, format.content, availableWidth, adjustedFont);

      // Re-add formatting markers to wrapped lines so renderFormattedTextLines can parse them
      // Match headers (# ## ###), unordered lists (- *), or ordered lists (1. 2. etc)
      const prefix = line.match(/^\s*(?:#{1,3}|[-*]|\d+\.)\s+/)?.[0] ?? '';
      for (const wrappedLine of wrappedLines) {
        allLines.push(prefix ? `${prefix}${wrappedLine}` : wrappedLine);
      }
    }

    const baseLineHeight = fontSize * LINE_HEIGHT_RATIO;

    const { textX } = resolveTextAlignPosition(
      textAlign,
      bounds.x + padding,
      center.x,
      bounds.x + bounds.width - padding
    );
    const startY = getCenteredTextStartY(center.y, baseLineHeight, allLines.length);

    renderFormattedTextLines(ctx, {
      lines: allLines,
      baseFontSize: fontSize,
      baseLineHeight,
      textX,
      startY,
      fontWeight,
      fontStyle,
      fontFamily,
    });
  }

  clone(): Shape {
    const ShapeClass = this.constructor as new (
      position: Point,
      size: Size,
      style?: Partial<ShapeStyle>,
      text?: string
    ) => Shape;
    const cloned = new ShapeClass(
      { x: this.position.x + CLIPBOARD.pasteOffset, y: this.position.y + CLIPBOARD.pasteOffset },
      { ...this.size },
      { ...this.style },
      this.text
    );
    cloned.textStyle = { ...this.textStyle };
    return cloned;
  }

  toJSON(): object {
    return buildElementJSON(this.id, this.type, {
      position: this.position,
      size: this.size,
      rotation: this.rotation,
      style: this.style,
      textStyle: this.textStyle,
      text: this.text,
    });
  }
}
